import type {
  AnyXYChartScale,
  XYChartAxisDirection,
} from '../types/xy-chart-internals.js';
import type { BarDatapoint, XYAccessorDataTypes } from '../types/xy-chart.js';

export interface HitBoxRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Compares domain endpoints rather than a datapoint's value, since a
// zero-value bar's value equals the baseline and can't reveal a direction.
function isValueIncreasingTowardSmallerPixel(scale: AnyXYChartScale): boolean {
  const domain = scale.domain();
  const a = Number(domain[0]);
  const b = Number(domain[domain.length - 1]);
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const numericScale = scale as (value: number) => number;
  return numericScale(lo) > numericScale(hi);
}

// Dates coerce to their epoch-ms number via unary +, which scales the same
// as passing the Date itself, so this covers numeric and time value axes.
function toNumeric(value: XYAccessorDataTypes | undefined): number | undefined {
  return value === undefined || typeof value === 'string' ? undefined : +value;
}

function getHorizontalBarHitBox(
  rect: HitBoxRect,
  datapoint: BarDatapoint,
  min: number,
  xScale: AnyXYChartScale,
): HitBoxRect {
  const { x, y, width, height } = rect;
  const numericXScale = xScale as (value: number) => number;
  const hitWidth = Math.max(width, min);
  const baselineX = toNumeric(datapoint.x0) ?? 0;
  const numericX1 = toNumeric(datapoint.x1);

  let growsRight: boolean;
  if (numericX1 === undefined) {
    // x1 missing or non-numeric (malformed data) — nothing to anchor
    // against, so default to the common case of growing away from 0.
    growsRight = true;
  } else {
    const scaledX1 = numericXScale(numericX1);
    const scaledBaseline = numericXScale(baselineX);
    // A zero-value bar has scaledX1 === scaledBaseline, which can't tell
    // us the growth direction — resolve it from the axis's own direction.
    growsRight =
      scaledX1 === scaledBaseline
        ? !isValueIncreasingTowardSmallerPixel(xScale)
        : scaledBaseline <= scaledX1;
  }

  return {
    x: growsRight ? x : x + width - hitWidth,
    y,
    width: hitWidth,
    height,
  };
}

function getVerticalBarHitBox(
  rect: HitBoxRect,
  datapoint: BarDatapoint,
  min: number,
  yScale: AnyXYChartScale,
): HitBoxRect {
  const { x, y, width, height } = rect;
  const numericYScale = yScale as (value: number) => number;
  const hitHeight = Math.max(height, min);
  const baselineY = toNumeric(datapoint.y0) ?? 0;
  const numericY1 = toNumeric(datapoint.y1);

  let growsUp: boolean;
  if (numericY1 === undefined) {
    // y1 missing or non-numeric (malformed data) — nothing to anchor
    // against, so default to the common case of growing away from 0.
    growsUp = true;
  } else {
    const scaledY1 = numericYScale(numericY1);
    const scaledBaseline = numericYScale(baselineY);
    // Same ambiguity as above, resolved via the axis's own direction.
    growsUp =
      scaledY1 === scaledBaseline
        ? isValueIncreasingTowardSmallerPixel(yScale)
        : scaledY1 <= scaledBaseline;
  }

  return {
    x,
    y: growsUp ? y + height - hitHeight : y,
    width,
    height: hitHeight,
  };
}

function getCornerAnchoredHitBox(rect: HitBoxRect, min: number): HitBoxRect {
  const { x, y, width, height } = rect;
  return {
    x: x - Math.max(0, min - width),
    y: y - Math.max(0, min - height),
    width: Math.max(width, min),
    height: Math.max(height, min),
  };
}

/**
 * Expands a bar's hit-box to `min` while keeping its baseline edge fixed,
 * so growth stays anchored on reversed axes (TTB/RTL) too.
 *
 * `baseline` identifies which coordinate is the bar's fixed positional axis
 * (`'x'` for vertical bars, `'y'` for horizontal, `undefined` for bars with
 * no single categorical axis) — it's derived from the chart's orientation
 * rather than the datapoint's field types, so this works the same whether
 * the axes are numeric, categorical, or time-based.
 */
export function getBarHitBox(
  rect: HitBoxRect,
  datapoint: BarDatapoint,
  min: number,
  xScale: AnyXYChartScale,
  yScale: AnyXYChartScale,
  baseline: XYChartAxisDirection,
): HitBoxRect {
  if (baseline === 'y') {
    return getHorizontalBarHitBox(rect, datapoint, min, xScale);
  }
  if (baseline === 'x') {
    return getVerticalBarHitBox(rect, datapoint, min, yScale);
  }

  return getCornerAnchoredHitBox(rect, min);
}
